// frontend/src/hooks/useOfflineSync.ts
// Hook personalizado para manejar sincronización offline

import { useState, useEffect, useCallback } from 'react';
import { storage, type StoredRegistro, type RegistroEnviado, type RegistroRechazado } from '../services/storage';
import { api, ApiError, isDefinitiveApiError, type SyncResultadoRegistro } from '../lib/api';
import { getMarcasEnVuelo, suscribirMarcasEnVuelo } from '../lib/marcasEnVuelo';

export interface ConnectionStatus {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSync: string | null;
}

type SyncState = 'idle' | 'syncing' | 'success' | 'error';

// Opciones de sincronizar():
//  - silent: true → no publica syncState/syncError (no abre el SyncModal de
//    TopAppBar). Se usa para la auto-sync tras cada marca. isSyncing y el
//    conteo de pendientes sí se actualizan.
//  - pull: false → solo push (no descarga la ventana completa del servidor).
// Sin argumentos: sync manual visible con pull.
export interface SincronizarOpciones {
  silent?: boolean;
  pull?: boolean;
}

export interface SincronizarResultado {
  exito: boolean;
  error?: string;
  // Registros rechazados definitivamente por el servidor en esta corrida
  rechazados: number;
}

interface OpcionesCorrida {
  visible: boolean;
  pull: boolean;
}

// ============================================
// ESTADO COMPARTIDO DE SINCRONIZACIÓN (nivel módulo)
// ============================================
// El hook se usa en varios componentes (App, TopAppBar, ...). El lock y el
// estado de "sincronizando" deben ser únicos para toda la app: si cada
// instancia tuviera su propio guard, dos sincronizar() concurrentes subirían
// los mismos registros y drenarían la misma cola dos veces.

interface SharedSyncState {
  isSyncing: boolean;
  syncState: SyncState;
  syncError: string | undefined;
  lastSync: string | null;
}

let sharedState: SharedSyncState = {
  isSyncing: false,
  syncState: 'idle',
  syncError: undefined,
  lastSync: null,
};

const listeners = new Set<(state: SharedSyncState) => void>();

function setSharedState(patch: Partial<SharedSyncState>): void {
  sharedState = { ...sharedState, ...patch };
  listeners.forEach((listener) => listener(sharedState));
}

// Sync en curso (lock compartido). Las llamadas concurrentes se agrupan: se
// espera la corrida actual y, si alguien pidió sincronizar mientras tanto, se
// hace UNA corrida adicional al terminar (para subir lo escrito durante la sync).
// La corrida adicional hace pull solo si algún llamador lo pidió, y es visible
// si algún llamador no era silencioso.
let syncEnCurso: Promise<SincronizarResultado> | null = null;
let syncRepetir = false;
let repetirPull = false;
let repetirVisible = false;

export function sincronizarCompartido(opts?: SincronizarOpciones): Promise<SincronizarResultado> {
  const pedido: OpcionesCorrida = {
    visible: opts?.silent !== true,
    pull: opts?.pull !== false,
  };

  if (syncEnCurso) {
    syncRepetir = true;
    repetirPull = repetirPull || pedido.pull;
    repetirVisible = repetirVisible || pedido.visible;
    return syncEnCurso;
  }

  syncEnCurso = (async () => {
    let corrida = pedido;
    let resultado: SincronizarResultado = { exito: false, rechazados: 0 };
    try {
      do {
        syncRepetir = false;
        resultado = await ejecutarSincronizacion(corrida);
        corrida = { visible: repetirVisible, pull: repetirPull };
        repetirPull = false;
        repetirVisible = false;
      } while (syncRepetir && navigator.onLine && localStorage.getItem('auth_token'));
    } finally {
      syncEnCurso = null;
      syncRepetir = false;
      repetirPull = false;
      repetirVisible = false;
      ultimaCorridaAt = Date.now();
    }
    return resultado;
  })();

  return syncEnCurso;
}

// Espera a que no haya ninguna corrida compartida en curso (o a que venza el
// timeout). Devuelve true si no quedó nada corriendo. Encadena mientras haya
// corridas (una corrida puede disparar una repetición). Lo usa main.tsx para no
// recargar la página en medio de un push (D6).
export function esperarSyncEnCurso(timeoutMs = 10000): Promise<boolean> {
  if (!syncEnCurso) return Promise.resolve(true);
  return new Promise((resolve) => {
    let terminado = false;
    const timer = setTimeout(() => {
      terminado = true;
      resolve(syncEnCurso === null);
    }, timeoutMs);
    const esperar = (): void => {
      if (terminado) return;
      const actual = syncEnCurso;
      if (!actual) {
        terminado = true;
        clearTimeout(timer);
        resolve(true);
        return;
      }
      actual.then(esperar, esperar);
    };
    esperar();
  });
}

// Pull de datos del servidor (también lock compartido para el pull inicial de
// cada instancia montada).
let pullEnCurso: Promise<boolean> | null = null;

// Pull inicial (al montar): se encola detrás de una sync en curso para no
// descargar un snapshot anterior a un push que todavía se está procesando.
async function pullInicialCompartido(): Promise<boolean> {
  if (syncEnCurso) {
    try {
      await syncEnCurso;
    } catch { /* ignorar: el pull se intenta igual */ }
  }
  return syncFromServerCompartido();
}

function syncFromServerCompartido(): Promise<boolean> {
  if (!pullEnCurso) {
    pullEnCurso = syncFromServer().finally(() => {
      pullEnCurso = null;
    });
  }
  return pullEnCurso;
}

// Fecha local YYYY-MM-DD de hace `dias` días (fallback si el backend no envía registrosDesde)
function fechaLocalHaceDias(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Devuelve true si el pull se completó correctamente.
async function syncFromServer(): Promise<boolean> {
  try {
    const clienteId = storage.generateClientId();
    const metadata = await storage.getSyncMetadata();
    const data = await api.getCompleteData(clienteId, metadata?.ultima_sincronizacion || undefined);

    if (!data.success) return false;

    // Merge cadetes (no destructivo: respeta ediciones locales pendientes).
    // El servidor envía la lista COMPLETA de cadetes activos del alcance del
    // usuario: se eliminan los locales (no pendientes) que ya no vienen.
    if (Array.isArray(data.data.cadetes)) {
      await storage.mergeServerCadetes(data.data.cadetes, { completo: true });
    }

    // Merge registros (no pisa pendientes). Si el servidor envió la ventana
    // completa, se eliminan los registros locales ya sincronizados de esa
    // ventana que no vinieron (borrados en el servidor).
    // snapshotAt: los registros subidos después de esta hora (push concurrente)
    // no se borran ni se pisan con el snapshot.
    const ventanaCompleta = data.data.registrosCompletos === true
      ? {
          desde: data.data.registrosDesde || fechaLocalHaceDias(30),
          hasta: '9999-12-31',
          snapshotAt: data.data.timestamp,
        }
      : undefined;
    await storage.saveRegistrosMerge(data.data.registrosRecientes, ventanaCompleta);

    // Guardar tipos de licencias (siempre se reemplazan)
    if (data.data.tiposLicencias) {
      await storage.setTiposLicencias(data.data.tiposLicencias);
    }

    // Merge licencias (no destructivo)
    if (data.data.licencias) {
      await storage.mergeServerLicencias(data.data.licencias);
    }

    // Cursor de sincronización: hora del SERVIDOR. Si el backend no la envía
    // (versión vieja) no se avanza: el reloj del dispositivo no es confiable.
    const serverTimestamp = data.data.timestamp;
    if (serverTimestamp) {
      await storage.saveSyncMetadata({
        cliente_id: clienteId,
        ultima_sincronizacion: serverTimestamp,
        registros_pendientes: data.data.sincronizacionesPendientes?.length ?? 0,
      });
      setSharedState({ lastSync: serverTimestamp });
    }

    // Avisar a las vistas al final del pull: las licencias y los tipos se
    // guardan después de los registros, y sin este aviso la lista no las veía
    // hasta el próximo cambio (p. ej. el botón P de un cadete con licencia).
    window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));

    return true;
  } catch (error) {
    console.error('Error al sincronizar desde servidor:', error);
    return false;
  }
}

function showSyncNotification(count: number) {
  // Crear notificación visual
  const notification = document.createElement('div');
  notification.className = 'toast';
  notification.innerHTML = `
    <svg class="w-5 h-5 text-status-present" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
    </svg>
    <span>${count} ${count === 1 ? 'registro sincronizado' : 'registros sincronizados'}</span>
  `;
  document.body.appendChild(notification);

  setTimeout(() => {
    notification.remove();
  }, 3000);
}

// Mensaje legible para errores transitorios
function mensajeErrorTransitorio(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.code === 'NETWORK_ERROR') return 'NETWORK_ERROR';
    if (error.code === 'SERVER_UNAVAILABLE') return 'Servidor no disponible, se reintentará';
    if (error.status === 401 || error.status === 403) return 'Sesión vencida o sin permisos';
  }
  return fallback;
}

// Errores por registro que indican un problema transitorio del servidor
// (base de datos caída, timeout, etc.): se reintentan en la próxima sync.
const ERROR_REGISTRO_TRANSITORIO = /timeout|timed out|ECONN|ETIMEDOUT|connection|conexi[oó]n|deadlock|too many clients|terminat|unavailable|no disponible/i;

function esRechazoTransitorio(res: SyncResultadoRegistro | undefined): boolean {
  if (!res) return true; // sin resultado para ese índice: reintentar
  if (res.transitorio === true || res.retryable === true) return true;
  if (res.transitorio === false || res.retryable === false) return false;
  return typeof res.error === 'string' && ERROR_REGISTRO_TRANSITORIO.test(res.error);
}

function textoRechazados(n: number): string {
  return `${n} ${n === 1 ? 'registro rechazado' : 'registros rechazados'} por el servidor`;
}

// Hay sesión utilizable: con token y sin un cambio de contraseña pendiente (en
// ese estado el servidor rechaza todo con 403; no sincronizar hasta cambiarla).
const haySesion = () => {
  if (!localStorage.getItem('auth_token')) return false;
  try {
    return JSON.parse(localStorage.getItem('auth_user') || '{}')?.debe_cambiar_password !== true;
  } catch {
    return true;
  }
};

// ============================================
// DISPARADORES AUTOMÁTICOS (nivel módulo, un solo listener por app)
// ============================================
// D3 del plan de migración (§1.3) / fase 0 (§3.1): hasta ahora el evento
// `online` solo cambiaba el estado y las marcas offline quedaban horas sin
// subir. Ahora, con sesión y pendientes > 0, se dispara una sync silenciosa
// (solo push) al volver la conexión (debounce ~2 s: las redes móviles oscilan)
// y al recuperar el foco / visibilidad si pasaron > 60 s desde la última
// corrida. Se instala UNA sola vez aunque el hook se monte en varios
// componentes, y respeta el lock compartido: si ya hay una corrida en curso no
// se encola otra (lo escrito durante la sync lo cubre la auto-sync por marca).
const DEBOUNCE_RECONEXION_MS = 2000;
const INTERVALO_MIN_FOCO_MS = 60_000;

// Fin de la última corrida compartida (manual o automática)
let ultimaCorridaAt = 0;
let timerReconexion: ReturnType<typeof setTimeout> | null = null;
let autoSyncVerificando = false;
let disparadoresInstalados = false;

// Disparador abortado por marcas en vuelo: se reintenta (una sola vez, con el
// mismo throttle que el disparador original) cuando el contador llega a 0.
interface ReintentoAuto {
  motivo: string;
  conThrottle: boolean;
}
let reintentoPorMarcas: ReintentoAuto | null = null;

function pasoElThrottleDeFoco(): boolean {
  return Date.now() - ultimaCorridaAt >= INTERVALO_MIN_FOCO_MS;
}

async function autoSincronizarSiHayPendientes(motivo: string, conThrottle = false): Promise<void> {
  if (autoSyncVerificando || syncEnCurso) return;
  if (!navigator.onLine || !haySesion()) return;
  // Con una marca en cuenta regresiva / esperando geolocalización NO se
  // sincroniza: un 401 durante la sync recargaría la página y la perdería
  // (api.ts handleUnauthorized). Se reintenta cuando el contador llegue a 0.
  if (getMarcasEnVuelo() > 0) {
    reintentoPorMarcas = { motivo, conThrottle };
    return;
  }
  autoSyncVerificando = true;
  try {
    const pendientes = await storage.getPendingTotalCount();
    // Re-chequear el lock: el conteo es asíncrono y otra corrida pudo arrancar
    if (pendientes === 0 || syncEnCurso) return;
    console.log(`[Sync] Sincronización automática (${motivo}): ${pendientes} pendiente(s)`);
    await sincronizarCompartido({ silent: true, pull: false });
  } catch (error) {
    console.warn(`[Sync] Falló la sincronización automática (${motivo}):`, error);
  } finally {
    autoSyncVerificando = false;
  }
}

function cancelarReconexionPendiente(): void {
  if (timerReconexion !== null) {
    clearTimeout(timerReconexion);
    timerReconexion = null;
  }
}

function instalarDisparadoresAutomaticos(): void {
  if (disparadoresInstalados || typeof window === 'undefined') return;
  disparadoresInstalados = true;

  // Reintento del disparador abortado por marcas en vuelo
  suscribirMarcasEnVuelo(() => {
    if (!reintentoPorMarcas || getMarcasEnVuelo() > 0) return;
    const { motivo, conThrottle } = reintentoPorMarcas;
    reintentoPorMarcas = null;
    if (conThrottle && !pasoElThrottleDeFoco()) return;
    void autoSincronizarSiHayPendientes(`${motivo}, tras marcas en vuelo`, conThrottle);
  });

  window.addEventListener('online', () => {
    cancelarReconexionPendiente();
    timerReconexion = setTimeout(() => {
      timerReconexion = null;
      void autoSincronizarSiHayPendientes('reconexión');
    }, DEBOUNCE_RECONEXION_MS);
  });

  // Si la red vuelve a caer dentro del debounce, no intentar
  window.addEventListener('offline', cancelarReconexionPendiente);

  const alRecuperarFoco = (motivo: string) => {
    if (document.visibilityState !== 'visible') return;
    if (!pasoElThrottleDeFoco()) return;
    void autoSincronizarSiHayPendientes(motivo, true);
  };
  document.addEventListener('visibilitychange', () => alRecuperarFoco('visibilidad'));
  window.addEventListener('focus', () => alRecuperarFoco('foco'));
}

// Una corrida completa: pull (opcional) → push presentismo → drenar cola de gestión.
// Con visible: false no se publica syncState/syncError (no abre el SyncModal).
async function ejecutarSincronizacion({ visible, pull }: OpcionesCorrida): Promise<SincronizarResultado> {
  if (!navigator.onLine) {
    if (visible) setSharedState({ syncState: 'error', syncError: 'Sin conexión a internet' });
    return { exito: false, error: 'Sin conexión a internet', rechazados: 0 };
  }

  setSharedState(
    visible
      ? { isSyncing: true, syncState: 'syncing', syncError: undefined }
      : { isSyncing: true }
  );

  let cortadoPorError = false;
  let errorMessage: string | undefined;
  let pullOk = !pull;
  let totalRechazados = 0;

  try {
    // PULL: traer datos del servidor (se omite con pull: false)
    if (pull) {
      pullOk = await syncFromServerCompartido();
      if (!pullOk) {
        // No se corta: el push puede funcionar igual, pero la sync no es exitosa.
        errorMessage = 'No se pudieron descargar datos del servidor';
      }
    }

    // PUSH presentismo: subir registros pendientes (incluye tombstones) en chunks
    if (haySesion()) {
      const pending = await storage.getPendingRegistros();
      const pendingUnicosPorCadeteFecha = new Map<string, StoredRegistro>();

      for (const registro of pending) {
        const key = `${registro.cadete_id}-${registro.fecha}`;
        pendingUnicosPorCadeteFecha.set(key, registro);
      }

      const pendingDedupe = Array.from(pendingUnicosPorCadeteFecha.values());

      if (pendingDedupe.length > 0) {
        const clienteId = storage.generateClientId();
        const CHUNK_SIZE = 50;
        let totalProcesados = 0;
        let totalFallidos = 0;

        for (let i = 0; i < pendingDedupe.length; i += CHUNK_SIZE) {
          if (!haySesion()) {
            cortadoPorError = true;
            errorMessage = 'Sesión vencida';
            break;
          }

          const chunk = pendingDedupe.slice(i, i + CHUNK_SIZE);
          try {
            const resultado = await api.syncOfflineRecords(clienteId, chunk);
            if (!resultado.success) {
              cortadoPorError = true;
              errorMessage = 'Error al subir registros de presentismo';
              break;
            }

            // Solo se marcan los registros confirmados por el servidor
            // (resultados en el mismo orden que el envío), con la versión
            // (rev) que efectivamente se subió. Los rechazados definitivos se
            // marcan sync_rechazado (dejan de estar pendientes); los fallos
            // transitorios quedan pendientes para la próxima sync.
            const resultados = resultado.data?.resultados;
            const confirmados: RegistroEnviado[] = [];
            const rechazados: RegistroRechazado[] = [];
            chunk.forEach((registro, idx) => {
              if (!registro.local_id) return;
              const res = Array.isArray(resultados) ? resultados[idx] : undefined;
              // Backend viejo sin resultados por registro: success global del chunk
              const ok = Array.isArray(resultados) ? res?.success === true : true;
              if (ok) {
                confirmados.push({ local_id: registro.local_id, rev: registro.rev });
              } else if (esRechazoTransitorio(res)) {
                totalFallidos++;
                console.warn('Registro no aplicado (transitorio), se reintentará:', registro.local_id, res?.error);
              } else {
                console.warn('Registro rechazado por el servidor:', registro.local_id, res?.error);
                rechazados.push({ local_id: registro.local_id, rev: registro.rev, error: res?.error });
              }
            });

            // Persistir progreso de este chunk antes de continuar
            await storage.markRegistrosAsSynced(confirmados, resultado.data?.timestamp);
            await storage.marcarRegistrosRechazados(rechazados);
            totalProcesados += confirmados.length;
            totalRechazados += rechazados.length;
          } catch (error) {
            if (isDefinitiveApiError(error) && haySesion()) {
              // El chunk entero fue rechazado de forma definitiva (400/403/...):
              // reintentar no lo resolverá. Se marcan como rechazados y se sigue.
              console.warn('Chunk de presentismo rechazado por el servidor:', error.status, error.message);
              const rechazados: RegistroRechazado[] = chunk
                .filter((r) => !!r.local_id)
                .map((r) => ({ local_id: r.local_id!, rev: r.rev, error: error.message }));
              await storage.marcarRegistrosRechazados(rechazados);
              totalRechazados += rechazados.length;
              continue;
            }
            console.warn('Sincronización de presentismo interrumpida:', error);
            cortadoPorError = true;
            errorMessage = mensajeErrorTransitorio(error, 'Error al sincronizar presentismo');
            break;
          }
        }

        if (totalProcesados > 0) {
          showSyncNotification(totalProcesados);
        }
        if (totalRechazados > 0 && !errorMessage) {
          errorMessage = textoRechazados(totalRechazados);
        }
        if (totalFallidos > 0 && !errorMessage) {
          errorMessage = `${totalFallidos} ${totalFallidos === 1 ? 'registro no se pudo subir' : 'registros no se pudieron subir'}, se reintentará`;
        }
      }
    }

    // PUSH gestión: drenar sync_queue en orden FIFO
    if (!cortadoPorError && haySesion()) {
      const queue = await storage.getQueue();

      for (const op of queue) {
        // Un 401 previo borra el token y recarga la página: no seguir
        // procesando (ni descartando) ops de la cola.
        if (!haySesion()) {
          cortadoPorError = true;
          errorMessage = 'Sesión vencida';
          break;
        }

        try {
          if (op.type === 'updateCadete') {
            const res = await api.updateCadete(op.payload.id, op.payload.data);
            if (!res.success) {
              cortadoPorError = true;
              errorMessage = 'Error al actualizar cadete';
              break;
            }
            await storage.markCadetesAsSynced([op.payload.id]);
            await storage.deleteQueueItem(op.id);
          } else if (op.type === 'createLicencia') {
            const res = await api.createLicencia(op.payload);
            if (!res.success) {
              cortadoPorError = true;
              errorMessage = 'Error al crear licencia';
              break;
            }
            // Reemplazar licencia temporal con la real del servidor
            const licencias = await storage.getLicencias();
            const tempId = op.payload._tempId as string | undefined;
            const updated = licencias.map((l: any) =>
              tempId && l.id === tempId ? { ...res.data, id: res.data.id } : l
            );
            await storage.setLicencias(updated);
            await storage.deleteQueueItem(op.id);
          } else if (op.type === 'deleteLicencia') {
            try {
              const res = await api.deleteLicencia(op.payload.id);
              if (!res.success) {
                cortadoPorError = true;
                errorMessage = 'Error al eliminar licencia';
                break;
              }
            } catch (deleteErr) {
              // 404: ya estaba eliminada en el servidor — se toma como éxito
              if (!(deleteErr instanceof ApiError && deleteErr.code === 'HTTP_ERROR' && deleteErr.status === 404)) {
                throw deleteErr;
              }
            }
            await storage.deleteQueueItem(op.id);
          } else if (op.type === 'updateGradoCompania') {
            const res = await api.updateGradoCompania(op.payload.compania, op.payload.grado);
            if (!res.success) {
              cortadoPorError = true;
              errorMessage = 'Error al actualizar grado/compañía';
              break;
            }
            await storage.mergeCadetes(res.data);
            await storage.deleteQueueItem(op.id);
          } else {
            // Op desconocida — saltar
            await storage.deleteQueueItem(op.id);
          }
        } catch (error) {
          console.warn('Error al procesar op de la cola:', op.type, error);

          if (isDefinitiveApiError(error) && haySesion()) {
            // Error definitivo (400/403/404/409/422): reintentar no lo resolverá.
            // Se descarta la op y se continúa con el resto.
            console.warn(`Op descartada por error definitivo del servidor: ${op.type} — ${error.status} ${error.message}`);
            try {
              if (op.type === 'createLicencia' && op.payload?._tempId) {
                // Ej. 400 por superposición: quitar la licencia temporal local
                const licencias = await storage.getLicencias();
                await storage.setLicencias(licencias.filter((l: any) => l.id !== op.payload._tempId));
              } else if (op.type === 'updateCadete' && op.payload?.id != null) {
                // Liberar el pending_sync para que el próximo pull traiga la versión del servidor
                await storage.markCadetesAsSynced([op.payload.id]);
              }
              await storage.deleteQueueItem(op.id);
            } catch { /* ignorar */ }
            if (!errorMessage) errorMessage = `Operación rechazada por el servidor (${op.type}): ${error.message}`;
            continue;
          }

          // Transitorio (red, timeout, 401/408/429/5xx, respuesta no-JSON)
          // o error local inesperado: detener y conservar la op para reintentar.
          cortadoPorError = true;
          errorMessage = mensajeErrorTransitorio(error, `Error al sincronizar (${op.type})`);
          break;
        }
      }

      // Cola drenada: un cadete con pending_sync sin op updateCadete en cola no
      // tiene nada que subir (p. ej. el grado masivo los marca pendientes pero
      // encola una sola op updateGradoCompania). Si quedara pendiente, el merge
      // ignoraría para siempre los cambios del servidor sobre ese cadete.
      if (!cortadoPorError) {
        try {
          const pendientes = await storage.getPendingCadetes();
          if (pendientes.length > 0) {
            const conOp = new Set(
              (await storage.getQueue())
                .filter((op) => op.type === 'updateCadete')
                .map((op) => Number(op.payload?.id))
            );
            const huerfanos = pendientes.map((c) => c.id).filter((id) => !conOp.has(Number(id)));
            await storage.markCadetesAsSynced(huerfanos);
          }
        } catch (err) {
          console.warn('No se pudieron liberar cadetes pendientes sin op:', err);
        }
      }
    }
  } catch (error) {
    console.error('Error inesperado en sincronizar():', error);
    cortadoPorError = true;
    errorMessage = 'Error inesperado durante la sincronización';
  }

  // Cleanup
  try {
    await storage.cleanupOldSyncedRegistros(30);
  } catch (err) {
    console.warn('Error en limpieza post-sincronización:', err);
  }

  const exito = !cortadoPorError && pullOk && !errorMessage;
  const error = exito ? undefined : errorMessage ?? 'Error durante la sincronización';

  if (exito) {
    setSharedState(
      visible
        ? { isSyncing: false, syncState: 'success', syncError: undefined }
        : { isSyncing: false }
    );
    window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
  } else {
    setSharedState(
      visible
        ? { isSyncing: false, syncState: 'error', syncError: error }
        : { isSyncing: false }
    );
  }

  return { exito, error, rechazados: totalRechazados };
}

export function useOfflineSync() {
  const [status, setStatus] = useState<ConnectionStatus>({
    isOnline: navigator.onLine,
    isSyncing: sharedState.isSyncing,
    pendingCount: 0,
    lastSync: sharedState.lastSync,
  });

  const [syncState, setSyncState] = useState<SyncState>(sharedState.syncState);
  const [syncError, setSyncError] = useState<string | undefined>(sharedState.syncError);

  // Suscripción al estado compartido de sincronización
  useEffect(() => {
    const listener = (state: SharedSyncState) => {
      setStatus((prev) => ({ ...prev, isSyncing: state.isSyncing, lastSync: state.lastSync }));
      setSyncState(state.syncState);
      setSyncError(state.syncError);
    };
    listeners.add(listener);
    listener(sharedState);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Actualizar estado de conexión
  useEffect(() => {
    let activo = true;

    const refreshPendingCount = async () => {
      const pendingCount = await storage.getPendingTotalCount();
      if (activo) setStatus((prev) => ({ ...prev, pendingCount }));
    };

    const handleOnline = () => {
      console.log('Conexión restaurada');
      // Solo actualiza el estado de esta instancia; la auto-sync al reconectar
      // la dispara el listener único de instalarDisparadoresAutomaticos().
      setStatus((prev) => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      console.log('Conexión perdida');
      setStatus((prev) => ({ ...prev, isOnline: false }));
    };

    const handleSyncDataChanged = () => {
      refreshPendingCount().catch((error) => {
        console.error('Error al actualizar pendientes locales:', error);
      });
    };

    const initSync = async () => {
      try {
        // Con sesión y conexión: pull inicial (compartido entre instancias)
        if (haySesion() && navigator.onLine) {
          await pullInicialCompartido();

          // Ejecutar limpieza de IndexedDB al inicializar
          try {
            await storage.cleanupOldSyncedRegistros(30);
          } catch (err) {
            console.warn('Error en limpieza inicial de IndexedDB:', err);
          }
        }

        // Actualizar conteo de pendientes y última sincronización
        const pendingCount = await storage.getPendingTotalCount();
        const metadata = await storage.getSyncMetadata();
        const lastSync = metadata?.ultima_sincronizacion || null;
        if (lastSync && lastSync !== sharedState.lastSync) {
          setSharedState({ lastSync });
        }

        if (activo) {
          setStatus((prev) => ({
            ...prev,
            isOnline: navigator.onLine,
            pendingCount,
            lastSync: sharedState.lastSync,
          }));
        }
      } catch (error) {
        console.error('Error al inicializar sincronización:', error);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('sgcc:sync-data-changed', handleSyncDataChanged as EventListener);

    // Auto-sync por reconexión / foco (idempotente: un solo listener por app)
    instalarDisparadoresAutomaticos();

    // Inicializar
    initSync();

    return () => {
      activo = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('sgcc:sync-data-changed', handleSyncDataChanged as EventListener);
    };
  }, []);

  // Devuelve Promise<void> (contrato con la UI); los rechazados se consultan
  // con storage.getRegistrosRechazados().
  const sincronizar = useCallback(async (opts?: SincronizarOpciones): Promise<void> => {
    // Ignorar argumentos que no sean opciones (ej. un evento de click)
    const opciones: SincronizarOpciones | undefined =
      opts && typeof opts === 'object' && !('nativeEvent' in opts) && !(opts instanceof Event)
        ? { silent: opts.silent, pull: opts.pull }
        : undefined;
    await sincronizarCompartido(opciones);
    // Refrescar conteo de pendientes al terminar
    try {
      const pendingCount = await storage.getPendingTotalCount();
      setStatus((prev) => ({ ...prev, pendingCount }));
    } catch (err) {
      console.warn('Error al actualizar pendientes:', err);
    }
  }, []);

  const resetSyncState = useCallback(() => {
    // No pisar el estado mientras hay una sync en curso
    if (sharedState.isSyncing) return;
    setSharedState({ syncState: 'idle', syncError: undefined });
  }, []);

  // forceSync kept as alias for backward compatibility
  const forceSync = sincronizar;

  return {
    ...status,
    forceSync,
    sincronizar,
    syncState,
    syncError,
    resetSyncState,
  };
}
